import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, Ship, HardHat, FileText, Users, Shield, 
  CheckCircle, ArrowRight, ClipboardList, AlertTriangle, 
  BarChart3, Anchor, Factory
} from 'lucide-react';
import './Industries.css';

const Industries: React.FC = () => {
  const industries = [
    {
      id: 'life-sciences',
      icon: <Building2 size={48} />,
      title: 'Life Sciences',
      subtitle: 'Pharma, Biotech, Medical Devices and CROs',
      description: 'Full lifecycle quality and document governance for organizations where compliance is non-negotiable.',
      features: [
        { icon: <FileText size={20} />, text: 'SOPs & Controlled Documents' },
        { icon: <Users size={20} />, text: 'Training Management' },
        { icon: <ClipboardList size={20} />, text: 'Change Control' },
        { icon: <AlertTriangle size={20} />, text: 'CAPA Management' },
        { icon: <Shield size={20} />, text: 'Audit Evidence Packaging' },
        { icon: <BarChart3 size={20} />, text: 'Quality Metrics & KPIs' },
      ],
      useCases: [
        'Document approval workflows with electronic signatures',
        'Automated training triggered by document revisions',
        'Deviation and OOS/OOT investigation tracking',
        'Supplier qualification and audit management',
        'Complaint handling with regulatory reporting'
      ],
      compliance: ['FDA 21 CFR Part 11', 'GxP', 'ISO 13485', 'ICH Q10'],
      gradient: 'life-sciences'
    },
    {
      id: 'marine',
      icon: <Ship size={48} />,
      title: 'Marine',
      subtitle: 'Fleet operations, maintenance records, and incident management',
      description: 'Governance for fleet operations and robust audit evidence for classification bodies.',
      features: [
        { icon: <Anchor size={20} />, text: 'Fleet Documentation' },
        { icon: <ClipboardList size={20} />, text: 'Maintenance Records' },
        { icon: <AlertTriangle size={20} />, text: 'Incident/Case Management' },
        { icon: <Shield size={20} />, text: 'Classification Audits' },
        { icon: <Users size={20} />, text: 'Crew Certification Tracking' },
        { icon: <FileText size={20} />, text: 'Regulatory Compliance' },
      ],
      useCases: [
        'Vessel maintenance scheduling and tracking',
        'Incident reporting and investigation',
        'Crew training and certification management',
        'Port state control inspection preparation',
        'Environmental compliance documentation'
      ],
      compliance: ['ISM Code', 'SOLAS', 'MARPOL', 'MLC 2006'],
      gradient: 'marine'
    },
    {
      id: 'mining',
      icon: <HardHat size={48} />,
      title: 'Mining',
      subtitle: 'Safety workflows, environmental reporting, and regulatory audits',
      description: 'Safety-first workflows with robust controlled document management for regulatory audits.',
      features: [
        { icon: <AlertTriangle size={20} />, text: 'Safety Workflows' },
        { icon: <Factory size={20} />, text: 'Environmental Reporting' },
        { icon: <ClipboardList size={20} />, text: 'Incident Capture' },
        { icon: <FileText size={20} />, text: 'Document Control' },
        { icon: <Shield size={20} />, text: 'Regulatory Audits' },
        { icon: <Users size={20} />, text: 'Workforce Training' },
      ],
      useCases: [
        'Safety permit and work authorization management',
        'Environmental impact monitoring and reporting',
        'Equipment inspection and maintenance logs',
        'Hazard identification and risk assessment',
        'Compliance audit trail documentation'
      ],
      compliance: ['MSHA', 'OSHA', 'ISO 45001', 'ISO 14001'],
      gradient: 'mining'
    }
  ];

  return (
    <div className="industries-page">
      {/* Hero */}
      <section className="industries-hero">
        <div className="container">
          <div className="industries-hero-content">
            <span className="badge">Industries We Serve</span>
            <h1>Tailored Solutions for Regulated Sectors</h1>
            <p>
              We serve industries where compliance, safety, and quality are not optional. 
              Our solutions are designed to meet the unique regulatory requirements of each sector.
            </p>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section">
        <div className="container">
          {industries.map((industry, index) => (
            <div 
              key={industry.id} 
              id={industry.id} 
              className={`industry-section ${index % 2 === 1 ? 'reverse' : ''}`}
            >
              <div className={`industry-header ${industry.gradient}`}>
                <div className="industry-icon">{industry.icon}</div>
                <div className="industry-title-block">
                  <h2>{industry.title}</h2>
                  <p className="industry-subtitle">{industry.subtitle}</p>
                </div>
              </div>

              <div className="industry-content">
                <div className="industry-main">
                  <p className="industry-description">{industry.description}</p>
                  
                  <div className="industry-features">
                    <h3>Key Capabilities</h3>
                    <div className="features-grid">
                      {industry.features.map((feature, idx) => (
                        <div key={idx} className="feature-item">
                          {feature.icon}
                          <span>{feature.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="industry-use-cases">
                    <h3>Common Use Cases</h3>
                    <ul>
                      {industry.useCases.map((useCase, idx) => (
                        <li key={idx}>
                          <CheckCircle size={18} />
                          {useCase}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="industry-sidebar">
                  <div className="compliance-box">
                    <h4>Compliance Standards</h4>
                    <div className="compliance-tags">
                      {industry.compliance.map((standard, idx) => (
                        <span key={idx} className="compliance-tag">{standard}</span>
                      ))}
                    </div>
                  </div>

                  <Link to="/demo" className="btn btn-primary">
                    Request Industry Demo <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="industries-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Don't See Your Industry?</h2>
            <p>
              Our platform is flexible and can be configured for various regulated industries. 
              Contact us to discuss your specific requirements.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Contact Us <ArrowRight size={18} />
              </Link>
              <Link to="/demo" className="btn btn-outline-white">
                Book a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;
